/**
 * Adopted for usage in InquiryTemplateConfigEditor
 * from dashboard/forms/FormUpdateVerificationTemplateVersion/FormSectionEnabledIdClassesByCountry
 */
import { Spacing } from '@persona-id/design-tokens';
import {
  CaretDownIcon,
  CircleHelpIcon,
  PlusIcon,
  TransferOutIcon,
} from '@persona-id/design-tokens/icons';
import Fuse from 'fuse.js';
import produce from 'immer';
import { omit, pullAt } from 'lodash/fp';
import { ChangeEvent, Fragment, memo, ReactElement, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, FormSection, WrappedFieldProps } from 'redux-form';
import styled from 'styled-components';

import { indexForCheck } from 'dashboard/apps/InquiryTemplateEditor/forms/utils';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import {
  FieldWrapper,
  FullSpanSubRowTD,
  SectionDescription,
  SubRowLabel,
  SubRowLabelTD,
  TableSubRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import { getVtmplvDisplayName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import ConfigureIdsTable from 'dashboard/apps/VerificationTemplateEditor/forms/FormSectionCountryConfigurations/ConfigureIdsTable';
import OverrideExpirationRow from 'dashboard/apps/VerificationTemplateEditor/OverrideExpirationRow';
import OverrideExtractionRow from 'dashboard/apps/VerificationTemplateEditor/OverrideExtractionRow';
import ContextMenu from 'dashboard/components/ContextMenu';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import SearchInput from 'dashboard/components-lib/SearchInput';
import TableLayout, {
  TableActionTD,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ComplianceRule, ComplianceRuleVersionType } from 'dashboard/models/compliance-rule';
import {
  GovernmentIdCountryCoverage,
  GovernmentIdCoverage,
  GovernmentIdSupport,
} from 'dashboard/models/government-id-coverage';
import {
  ExpirationCheckConfig,
  ExpirationThresholdConfig,
} from 'dashboard/models/verification-check-config/id-expiration-detection';
import {
  ExtractionCheckConfig,
  RequiredPropertyConfig,
} from 'dashboard/models/verification-check-config/id-extracted-properties-detection';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import {
  CountryCodeLower,
  GovernmentIdEnabledIdClassByCountries,
  GovernmentIdEnabledIdClassByCountryEntry,
  GovernmentIdSide,
  RequiredDevice,
  RESTRICTED_COUNTRIES,
  VerificationTemplateVersion,
  VerificationTemplateVersionGovernmentIdAttributes,
} from 'dashboard/models/verification-template-version';
import { setComplianceRules } from 'dashboard/redux/actions/compliance-rules';
import { setGovernmentIdCoverage } from 'dashboard/redux/actions/government-id-coverage';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { TransitionDuration } from 'design-system/variables/transitions';
import { GOVERNMENT_ID_DISPLAY_STRINGS, GovernmentIdClass } from 'lib/constants/governmentIds';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import useApiLoader from 'lib/hooks/useApiLoader';
import useDebounced from 'lib/hooks/useDebounced';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { ApiEndpoint } from 'lib/interfaces/api';
import { withMessage } from 'lib/utils/formValidation';
import { CheckName } from 'lib/verificationCheck';

type GovernmentIdOptions = GovernmentIdEnabledIdClassByCountryEntry;

type CountryRowElement = ReactElement<CountryRowProps>;
type StatusFilter = 'all' | 'enabled' | 'disabled' | 'restricted';

const FILTER_OPTIONS: { label: string; value: StatusFilter }[] = [
  {
    label: 'All countries',
    value: 'all',
  },
  {
    label: 'Enabled countries',
    value: 'enabled',
  },
  {
    label: 'Disabled countries',
    value: 'disabled',
  },
  {
    label: 'Restricted countries',
    value: 'restricted',
  },
];

export const REQUIRES_SIDES_OPTIONS: { label: string; value: GovernmentIdSide }[] = [
  { label: 'Front', value: GovernmentIdSide.Front },
  { label: 'Back', value: GovernmentIdSide.Back },
  { label: 'Barcode', value: GovernmentIdSide.BarcodePdf417 },
  { label: 'Passport signature', value: GovernmentIdSide.PassportSignature },
];

const createCountryCoverageTuples = (
  countryCoverages: GovernmentIdCountryCoverage[],
  extractionRequired: boolean,
): [CountryCodeLower, GovernmentIdOptions[]][] => {
  return countryCoverages.map((governmentIdCountryCoverage: GovernmentIdCountryCoverage) => {
    const otherIdCoverages = governmentIdCountryCoverage['other_id_support'] || [];
    let idCoverages = [
      governmentIdCountryCoverage['dl_support'],
      governmentIdCountryCoverage['id_support'],
      governmentIdCountryCoverage['pp_support'],
    ].concat(otherIdCoverages);

    if (extractionRequired) {
      idCoverages = idCoverages.filter((support) => support != null && support.has_extraction);
    } else {
      idCoverages = idCoverages.filter(Boolean);
    }

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const idCoverageJson = idCoverages.map(generateIdCoverageJson);

    return [
      governmentIdCountryCoverage['country_code'].toLowerCase() as CountryCodeLower,
      idCoverageJson,
    ];
  });
};

const generateIdCoverageJson = (idCoverage: GovernmentIdSupport): GovernmentIdOptions => ({
  class: idCoverage.class,
  'requires-sides': idCoverage['requires_back']
    ? [GovernmentIdSide.Front, GovernmentIdSide.Back]
    : [GovernmentIdSide.Front],
  'requires-device': RequiredDevice.None,
  'accepted-classes': [idCoverage.class],
});

const getRestrictedCountries = (
  complianceRules: ComplianceRule[],
  isCubanComplianceIdFFEnabled: boolean,
): CountryCodeLower[] => {
  const hasActiveCubanIdRule = complianceRules.find(
    (rule) =>
      rule.attributes['version-type'] === ComplianceRuleVersionType.CubanId &&
      rule.attributes.status === 'active',
  );
  return hasActiveCubanIdRule && isCubanComplianceIdFFEnabled
    ? RESTRICTED_COUNTRIES.filter((country) => country !== 'cu')
    : RESTRICTED_COUNTRIES;
};

interface CountryCodeName {
  countryCode: CountryCodeLower;
  name: string;
}

const TitleContainer = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  grid-gap: ${Spacing[3]};
  width: 100%;
  align-items: center;
  margin-bottom: ${Spacing[3]};
`;

const TableContainer = styled.div`
  max-height: 740px; /* show 12 rows */
  overflow: auto;
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[3]};
`;

interface FormSectionCountryConfigurationsProps {
  name: string;
  verificationTemplateVersion: VerificationTemplateVersion;
  enabledIdClassesByCountry: GovernmentIdEnabledIdClassByCountries;
  verificationChecks: VerificationCheckConfig[];
  accessForTierObj: {
    hasAccessForFreeTier: boolean;
    hasAccessForBasicTier: boolean;
  };
  disabled: boolean;
  isFormPristine: boolean;
}

const FormSectionCountryConfigurations = ({
  name,
  verificationTemplateVersion,
  enabledIdClassesByCountry,
  verificationChecks,
  accessForTierObj,
  disabled,
  isFormPristine,
}: FormSectionCountryConfigurationsProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSetSearchTerm] = useDebounced(setSearchTerm, 500, {
    leading: false,
    trailing: true,
  });
  const governmentIdCoverage = useSelector((state: RootState) => {
    return state.governmentIdCoverage.governmentIdCoverage;
  });

  const complianceRules = useSelector((state: RootState) => {
    return state.complianceRules.complianceRules;
  });

  const isComplianceCubanIdFFEnabled = useFeatureFlag('dashboard:compliance-cuban-id');

  const { hasAccessForFreeTier, hasAccessForBasicTier } = accessForTierObj;

  const dispatch = useDispatch();
  useApiLoader({
    loader: async () => {
      const [coverageResponse, complianceRulesResponse] = await Promise.all([
        SessionClientDashboard.get<GovernmentIdCoverage>(
          ApiEndpoint.VerificationGovernmentId,
          'coverage',
          '',
        ),
        isComplianceCubanIdFFEnabled
          ? SessionClientDashboard.listResource<ComplianceRule>(ApiEndpoint.ComplianceRule, {})
          : Promise.resolve({ data: [] }),
      ]);

      dispatch(setGovernmentIdCoverage(coverageResponse));
      dispatch(setComplianceRules(complianceRulesResponse?.data || []));
    },
    skip:
      governmentIdCoverage != null &&
      complianceRules != null &&
      isComplianceCubanIdFFEnabled != null,
    deps: [governmentIdCoverage, complianceRules, isComplianceCubanIdFFEnabled],
    onError: () =>
      dispatch(
        showMessage(
          'Failed to load government ID coverage or compliance rules.',
          MessageType.Failure,
        ),
      ),
    onNetworkError: () =>
      dispatch(
        showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
      ),
  });

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const handleSetStatusFilter = (statusFilters: StatusFilter) => setStatusFilter(statusFilters);

  const countryCoverages = useMemo(
    () => (governmentIdCoverage ? governmentIdCoverage['country_coverages'] : []),
    [governmentIdCoverage],
  );

  const allCountries = useMemo(
    () =>
      countryCoverages.map((governmentIdCountryCoverage) => {
        const countryCode = governmentIdCountryCoverage['country_code'] as CountryCode;
        return {
          countryCode: countryCode.toLowerCase() as CountryCodeLower,
          name: COUNTRIES[countryCode],
        };
      }),
    [countryCoverages],
  );

  /**
   * Return all countries supported by Scanner.
   */
  const defaultCountryIdClasses = useMemo<Record<CountryCodeLower, GovernmentIdOptions[]>>(
    () =>
      Object.fromEntries(createCountryCoverageTuples(countryCoverages, false)) as Record<
        CountryCodeLower,
        GovernmentIdOptions[]
      >,
    [countryCoverages],
  );

  const restrictedCountries = useMemo(
    () => getRestrictedCountries(complianceRules, isComplianceCubanIdFFEnabled),
    [complianceRules, isComplianceCubanIdFFEnabled],
  );

  const nonRestrictedCountriesAndIds = useMemo<Record<CountryCodeLower, GovernmentIdOptions[]>>(
    () =>
      omit(restrictedCountries, defaultCountryIdClasses) as Record<
        CountryCodeLower,
        GovernmentIdOptions[]
      >,
    [defaultCountryIdClasses, restrictedCountries],
  );

  const countriesAndIdsWithExtractionSupport = useMemo<
    Record<CountryCodeLower, GovernmentIdOptions[]>
  >(
    () =>
      Object.fromEntries(createCountryCoverageTuples(countryCoverages, true)) as Record<
        CountryCodeLower,
        GovernmentIdOptions[]
      >,
    [countryCoverages],
  );

  const countryToIdClasses: Record<CountryCodeLower, GovernmentIdClass[]> | null = useMemo(
    () =>
      enabledIdClassesByCountry != null
        ? (Object.fromEntries(
            Object.entries(enabledIdClassesByCountry)
              .filter(([, enabledIdClasses]) => enabledIdClasses != null)
              .map(([countryCode, enabledIdClasses]) => [
                countryCode,
                enabledIdClasses.map(({ class: idClass }) => idClass),
              ]),
          ) as Record<CountryCodeLower, GovernmentIdClass[]>)
        : null,
    [enabledIdClassesByCountry],
  );

  const handleExportEnabledIdClassesByCountryCsv = useCallback(async () => {
    // Correct headers with each value properly separated by commas
    const headers = [
      'Country Code',
      'Country',
      'ID Class Code',
      'ID Class',
      'Required Sides',
      'Accepted Id Classes',
      'Enabled',
    ];

    // Correctly format each row and quote values that may contain commas
    const checksCsvData = Object.entries(countriesAndIdsWithExtractionSupport).map(
      ([countryCode, idClasses]) =>
        idClasses
          .map(({ class: idClassCode }) => {
            const idClassProperties = enabledIdClassesByCountry[
              countryCode as CountryCodeLower
            ]?.find((enabledIdClass) => enabledIdClass.class === idClassCode);
            const countryName = allCountries.find(
              ({ countryCode: code }) => code === countryCode,
            )?.name;
            const idClass = idClassCode ? GOVERNMENT_ID_DISPLAY_STRINGS[idClassCode] : '-';
            const enabled = idClassProperties != null;
            const requiresSides = enabled ? idClassProperties['requires-sides'] : [];
            const acceptedClasses = enabled ? idClassProperties['accepted-classes'] : [];
            return [
              countryCode,
              `"${countryName}"`,
              idClassCode,
              `"${idClass}"`,
              `"${requiresSides.join(',')}"`, // Ensure commas in the array are handled with quotes
              `"${acceptedClasses.join(',')}"`, // Same here
              enabled ? 'yes' : 'no',
            ].join(',');
          })
          .join('\n'),
    );

    // Create a CSV blob and trigger download
    const blob = new Blob([[headers.join(','), ...checksCsvData].join('\n')], { type: 'text/csv' });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const displayName = getVtmplvDisplayName(verificationTemplateVersion);
    link.download = `${verificationTemplateVersion.id}_enabled_countries_and_id_types (${displayName}).csv`;
    link.href = downloadUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [
    allCountries,
    countriesAndIdsWithExtractionSupport,
    enabledIdClassesByCountry,
    verificationTemplateVersion,
  ]);

  if (countryToIdClasses == null) {
    return null;
  }

  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={
            <Tooltip
              side='top'
              trigger={
                <Button
                  width='auto'
                  variant='basic'
                  id='verfication-checks-table__export-button'
                  aria-label='Export'
                  prefixIcon={<TransferOutIcon />}
                  disabled={!isFormPristine}
                  onClick={handleExportEnabledIdClassesByCountryCsv}
                >
                  Export
                </Button>
              }
              disabled={isFormPristine}
            >
              Save your changes to export verification checks data.
            </Tooltip>
          }
        >
          <HorizontalContainer>
            Enabled countries and ID types
            {!hasAccessForFreeTier && (
              <ProductUpsellTooltip
                side='top'
                accessTier={EditorAccessTier.InquiriesBasic}
                learnMoreId='gov-id-id-countries-config'
                trigger={
                  <div>
                    <InquiriesGatingSticker
                      dataMarketingIdSuffix='gov-id-id-countries-config'
                      stickerType={EnhancedGatingStickerType.Limited}
                    />
                  </div>
                }
              />
            )}
          </HorizontalContainer>
        </Card.Title>
        <SectionDescription>
          Select which countries and ID types to enable. Additionally, override check configurations
          that were set in the table above.{' '}
          <a
            target='_blank'
            rel='noreferrer'
            href='/dashboard/resources/coverage-map/government-id'
          >
            Learn more
          </a>
        </SectionDescription>
        <TitleContainer>
          <SearchInput
            size='md'
            title={null}
            value={searchTerm}
            handleInput={debouncedSetSearchTerm}
            placeholder='Search by Country'
          />
          <Select value={statusFilter} onValueChange={handleSetStatusFilter} portal>
            {FILTER_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </TitleContainer>
        <TableContainer>
          <Field
            name={name}
            component={EnabledIdClassesTable}
            enabledIdClassesByCountry={enabledIdClassesByCountry}
            verificationChecks={verificationChecks}
            allCountries={allCountries}
            defaultCountryIdClasses={defaultCountryIdClasses}
            nonRestrictedCountriesAndIds={nonRestrictedCountriesAndIds}
            countriesAndIdsWithExtractionSupport={countriesAndIdsWithExtractionSupport}
            countryToIdClasses={countryToIdClasses}
            searchTerm={searchTerm}
            statusFilter={statusFilter}
            hasAccessForBasicTier={hasAccessForBasicTier}
            disabled={disabled}
            restrictedCountries={restrictedCountries}
          />
        </TableContainer>
      </Card.Body>
    </Card>
  );
};

const TableTHWithIcon = styled(TableTH)`
  svg {
    display: inline;
  }
`;

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: ${Spacing[4]};
  margin-left: auto;
`;

const CountryNameContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CaretContainer = styled.div`
  display: flex;
  width: ${Spacing[4]};
  height: ${Spacing[4]};
  align-self: center;
`;

const StyledIconCaret = styled(CaretDownIcon)<{ expanded: boolean; disabled: boolean }>`
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(${({ expanded }) => (expanded ? '0' : '-90deg')});
  justify-self: center;
  color: ${({ theme }) => theme.color.text.primary};
  visibility: ${({ disabled }) => (disabled ? 'hidden' : 'visible')};
`;

const TableHeaderButton = styled(Button).attrs(() => ({
  variant: 'basic',
  width: 'auto',
}))`
  display: inline-block;
  height: 32px;
  margin-right: ${Spacing[2]};
  line-height: 0;
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
`;

const StyledCheckbox = styled(Checkbox)`
  display: inline-block;
  vertical-align: middle;
  margin-right: ${Spacing[4]};
`;

interface EnabledIdClassesTableProps {
  enabledIdClassesByCountry: GovernmentIdEnabledIdClassByCountries;
  verificationChecks: VerificationCheckConfig[];
  allCountries: Array<CountryCodeName>;
  defaultCountryIdClasses: Record<string, GovernmentIdOptions[]>;
  nonRestrictedCountriesAndIds: Record<string, GovernmentIdOptions[]>;
  countriesAndIdsWithExtractionSupport: Record<string, GovernmentIdOptions[]>;
  countryToIdClasses: Record<CountryCodeLower, GovernmentIdClass[]>;
  searchTerm: string;
  statusFilter: StatusFilter;
  hasAccessForBasicTier: boolean;
  disabled: boolean;
  restrictedCountries: CountryCodeLower[];
}

interface IFilteredRows {
  statusFilter: StatusFilter;
  allRows: CountryRowElement[];
  enabledRows: CountryRowElement[];
  disabledRows: CountryRowElement[];
  restrictedCountries: CountryCodeLower[];
}

const filteredRows = ({
  statusFilter,
  allRows,
  enabledRows,
  disabledRows,
  restrictedCountries,
}: IFilteredRows): CountryRowElement[] => {
  switch (statusFilter) {
    case 'enabled':
      return enabledRows;
    case 'disabled':
      return disabledRows;
    case 'restricted':
      return allRows.filter((row) => {
        return restrictedCountries.includes(row.props.countryCode);
      });
    default:
      return allRows;
  }
};

type ExpandedCountriesRecord = Record<CountryCodeLower, boolean>;

const EnabledIdClassesTable = memo(function EnabledIdClassesTable({
  input,
  enabledIdClassesByCountry,
  verificationChecks,
  allCountries,
  defaultCountryIdClasses,
  nonRestrictedCountriesAndIds,
  countriesAndIdsWithExtractionSupport,
  countryToIdClasses,
  searchTerm,
  statusFilter,
  hasAccessForBasicTier,
  disabled,
  restrictedCountries,
}: EnabledIdClassesTableProps & WrappedFieldProps) {
  // used for enable, disable, reset country defaults
  const defaultExpandedCountries = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(countryToIdClasses).map((countryCode) => [countryCode, false]),
      ) as ExpandedCountriesRecord,
    [countryToIdClasses],
  );

  const [
    displayConfigureIdsModal,
    { enable: showConfigureIdsModal, disable: hideConfigureIdsModal },
  ] = useToggle(false);

  const [checkedCountries, setCheckedCountries] = useState<CountryCodeLower[]>([]);

  /**
   * Generates a "nested" table where the first level is country and the second level is ID type.
   */
  const [expandedCountries, setExpandedCountries] =
    useState<ExpandedCountriesRecord>(defaultExpandedCountries);

  // The enabled countries and ID types table controls the 'IdExtractedPropertiesDetection' and 'IdExpiredDetection' checks.
  // Those following checks have per country settings that are not directly controlled by the verification checks table
  // because of UI limitations, therefore we add that functionality to this component.
  const extractionIndex = indexForCheck(
    CheckName.IdExtractedPropertiesDetection,
    verificationChecks,
  );
  const expirationIndex = indexForCheck(CheckName.IdExpiredDetection, verificationChecks);

  const toggleCountryRow = useCallback(
    (countryCode: CountryCodeLower) =>
      setExpandedCountries({
        ...expandedCountries,
        [countryCode]: !expandedCountries[countryCode],
      }),
    [expandedCountries],
  );

  const setEnabledIdClassesByCountry = useCallback(
    (
      enabledIdClassesByCountry: Record<string, GovernmentIdOptions[]>,
      verificationTemplateVersionAttributes: VerificationTemplateVersionGovernmentIdAttributes,
    ) =>
      produce(
        verificationTemplateVersionAttributes,
        (draft: VerificationTemplateVersionGovernmentIdAttributes) => {
          draft['enabled-id-classes-by-country'] =
            enabledIdClassesByCountry as GovernmentIdEnabledIdClassByCountries;
        },
      ),
    [],
  );

  const setEnabledIdClassesForCountry = useCallback(
    (
      countryCode: CountryCodeLower,
      governmentIdOptions: GovernmentIdOptions[],
      verificationTemplateVersionAttributes: VerificationTemplateVersionGovernmentIdAttributes,
    ) =>
      produce(
        verificationTemplateVersionAttributes,
        (draft: VerificationTemplateVersionGovernmentIdAttributes) => {
          draft['enabled-id-classes-by-country'][countryCode] = governmentIdOptions;
        },
      ),
    [],
  );

  const setEnabledIdClassForCountry = useCallback(
    (
      countryCode: CountryCodeLower,
      idx: number,
      governmentIdOptions: GovernmentIdOptions,
      verificationTemplateVersionAttributes: VerificationTemplateVersionGovernmentIdAttributes,
    ) =>
      produce(
        verificationTemplateVersionAttributes,
        (draft: VerificationTemplateVersionGovernmentIdAttributes) => {
          draft['enabled-id-classes-by-country'][countryCode][idx] = governmentIdOptions;
        },
      ),
    [],
  );

  const setExpirationThresholds = useCallback(
    (
      expirationThresholds: ExpirationThresholdConfig[],
      verificationTemplateVersionAttributes: VerificationTemplateVersionGovernmentIdAttributes,
    ) =>
      produce(
        verificationTemplateVersionAttributes,
        (draft: VerificationTemplateVersionGovernmentIdAttributes) => {
          const expirationCheck =
            expirationIndex !== -1 &&
            (draft['verification-checks'][expirationIndex] as ExpirationCheckConfig);
          if (expirationCheck) {
            expirationCheck['expiration-thresholds'] = expirationThresholds;
            draft['verification-checks'][expirationIndex] = expirationCheck;
          }
        },
      ),
    [expirationIndex],
  );

  const setRequiredProperties = useCallback(
    (
      requiredProperties: RequiredPropertyConfig[],
      verificationTemplateVersionAttributes: VerificationTemplateVersionGovernmentIdAttributes,
    ) =>
      produce(
        verificationTemplateVersionAttributes,
        (draft: VerificationTemplateVersionGovernmentIdAttributes) => {
          const extractionCheck =
            extractionIndex !== -1 &&
            (draft['verification-checks'][extractionIndex] as ExtractionCheckConfig);
          if (extractionCheck) {
            extractionCheck['required-properties'] = requiredProperties;
            draft['verification-checks'][extractionIndex] = extractionCheck;
          }
        },
      ),
    [extractionIndex],
  );

  const handleOnlyEnableCountriesWithExtractionSupport = useCallback(
    () =>
      input.onChange(
        setEnabledIdClassesByCountry(countriesAndIdsWithExtractionSupport, input.value),
      ),
    [input, setEnabledIdClassesByCountry, countriesAndIdsWithExtractionSupport],
  );

  const handleResetAllToDefault = useCallback(
    () => input.onChange(setEnabledIdClassesByCountry(nonRestrictedCountriesAndIds, input.value)),
    [input, nonRestrictedCountriesAndIds, setEnabledIdClassesByCountry],
  );

  const handleDisableRestrictedCountries = useCallback(
    () =>
      input.onChange(
        setEnabledIdClassesByCountry(
          omit(restrictedCountries, enabledIdClassesByCountry),
          input.value,
        ),
      ),
    [input, enabledIdClassesByCountry, restrictedCountries, setEnabledIdClassesByCountry],
  );

  const handleDisableAll = useCallback(
    () => input.onChange(setEnabledIdClassesByCountry({}, input.value)),
    [input, setEnabledIdClassesByCountry],
  );

  const onCountryCheck = useCallback(
    (e: ChangeEvent<HTMLInputElement>, countryCode: CountryCodeLower) => {
      const newCountries = Array.from(checkedCountries);
      if (e.target.checked) {
        newCountries.push(countryCode);
        setCheckedCountries(newCountries);
      } else {
        const countryIndex = newCountries.findIndex((country) => countryCode === country);
        newCountries.splice(countryIndex, 1);
        setCheckedCountries(newCountries);
      }
    },
    [checkedCountries, setCheckedCountries],
  );

  // COUNTRY CODE HANDLERS
  const handleEnableCountry = useCallback(
    (countryCode: CountryCodeLower) =>
      input.onChange(
        setEnabledIdClassesForCountry(
          countryCode,
          defaultCountryIdClasses[countryCode],
          input.value,
        ),
      ),
    [input, setEnabledIdClassesForCountry, defaultCountryIdClasses],
  );

  const handleDisableCountry = useCallback(
    (countryCode: CountryCodeLower) => {
      toggleCountryRow(countryCode);
      input.onChange(
        setEnabledIdClassesByCountry(omit(countryCode, enabledIdClassesByCountry), input.value),
      );
    },
    [input, toggleCountryRow, setEnabledIdClassesByCountry, enabledIdClassesByCountry],
  );

  const handleResetCountryToDefault = useCallback(
    (countryCode: CountryCodeLower) => {
      const extractionCheck =
        extractionIndex !== -1 && (verificationChecks[extractionIndex] as ExtractionCheckConfig);
      const expirationCheck =
        expirationIndex !== -1 && (verificationChecks[expirationIndex] as ExpirationCheckConfig);
      // chaining changes because of a race condition, if you apply the changes one after another each change will override the previous one
      input.onChange(
        setRequiredProperties(
          extractionCheck
            ? extractionCheck['required-properties'].filter(
                (config) => config['country-code'] !== countryCode.toUpperCase(),
              )
            : [],
          setExpirationThresholds(
            expirationCheck
              ? expirationCheck['expiration-thresholds'].filter(
                  (config) => config['country-code'] !== countryCode.toUpperCase(),
                )
              : [],
            setEnabledIdClassesForCountry(
              countryCode,
              defaultCountryIdClasses[countryCode],
              input.value,
            ),
          ),
        ),
      );
    },
    [
      input,
      setRequiredProperties,
      extractionIndex,
      verificationChecks,
      setExpirationThresholds,
      expirationIndex,
      setEnabledIdClassesForCountry,
      defaultCountryIdClasses,
    ],
  );

  // ID CLASS HANDLERS
  const handleEnableIdClass = useCallback(
    (countryCode: CountryCodeLower, idClass: GovernmentIdClass) => {
      const defaultValue = defaultCountryIdClasses[countryCode].find(
        (idClassConfig) => idClassConfig.class === idClass,
      );

      const enabledIdClasses = input.value['enabled-id-classes-by-country'][
        countryCode
      ] as GovernmentIdOptions[];
      const newValue = enabledIdClasses
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .concat(defaultValue)
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .sort((a, b) => a && b && a.class.localeCompare(b.class));

      input.onChange(setEnabledIdClassesForCountry(countryCode, newValue, input.value));
    },
    [input, defaultCountryIdClasses, setEnabledIdClassesForCountry],
  );

  /**
   * Note that this can result in a country having a value of [].
   * We need to strip this out when post processing.
   */
  const handleDisableIdClass = useCallback(
    (countryCode: CountryCodeLower, idClass: GovernmentIdClass) => {
      const idx = countryToIdClasses[countryCode].findIndex(
        (idClassCompare) => idClass === idClassCompare,
      );
      const enabledIdClasses = input.value['enabled-id-classes-by-country'][countryCode];

      input.onChange(
        setEnabledIdClassesForCountry(countryCode, pullAt(idx, enabledIdClasses), input.value),
      );
    },
    [input, countryToIdClasses, setEnabledIdClassesForCountry],
  );

  const handleResetIdClassToDefault = useCallback(
    (countryCode: CountryCodeLower, idClass: GovernmentIdClass) => {
      const defaultValue = defaultCountryIdClasses[countryCode].find(
        (idClassConfig) => idClassConfig.class === idClass,
      );
      const idx = countryToIdClasses[countryCode].findIndex(
        (idClassCompare) => idClass === idClassCompare,
      );

      const extractionCheck =
        extractionIndex !== -1 && (verificationChecks[extractionIndex] as ExtractionCheckConfig);
      const expirationCheck =
        expirationIndex !== -1 && (verificationChecks[expirationIndex] as ExpirationCheckConfig);
      // chaining changes because of a race condition, if you apply the changes one after another each change will override the previous one
      input.onChange(
        setExpirationThresholds(
          expirationCheck
            ? expirationCheck['expiration-thresholds'].filter(
                (config) =>
                  config['country-code'] !== countryCode.toUpperCase() ||
                  config['id-class'] !== idClass,
              )
            : [],
          setRequiredProperties(
            extractionCheck
              ? extractionCheck['required-properties'].filter(
                  (config) =>
                    config['country-code'] !== countryCode.toUpperCase() ||
                    config['id-class'] !== idClass,
                )
              : [],
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setEnabledIdClassForCountry(countryCode, idx, defaultValue, input.value),
          ),
        ),
      );
    },
    [
      defaultCountryIdClasses,
      countryToIdClasses,
      input,
      setExpirationThresholds,
      expirationIndex,
      verificationChecks,
      setRequiredProperties,
      extractionIndex,
      setEnabledIdClassForCountry,
    ],
  );

  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: false,
      threshold: 0.3,
      ignoreLocation: true,
      maxPatternLength: 32,
      minMatchCharLength: 2,
      keys: [
        { name: 'name', weight: 0.8 },
        { name: 'countryCode', weight: 0.2 },
      ],
    };
    return new Fuse(allCountries, fuseOptions);
  }, [allCountries]);

  const searchResults: Set<CountryCodeLower> | null = useMemo(
    () =>
      searchTerm !== ''
        ? new Set(fuse.search(searchTerm).map(({ item }) => item.countryCode))
        : null,
    [fuse, searchTerm],
  );

  const {
    allRows,
    enabledRows,
    disabledRows,
  }: {
    allRows: CountryRowElement[];
    enabledRows: CountryRowElement[];
    disabledRows: CountryRowElement[];
  } = useMemo(() => {
    const enabledCountries = new Set<CountryCodeLower>(
      Object.keys(countryToIdClasses) as CountryCodeLower[],
    );
    const enabledRows: CountryRowElement[] = [];
    const disabledRows: CountryRowElement[] = [];
    const allRows: CountryRowElement[] = [];

    allCountries.forEach(({ countryCode, name }) => {
      if (searchResults == null || searchResults.has(countryCode)) {
        const countryIsDisabled = !enabledCountries.has(countryCode);
        const countryRows = [];
        // Push "header" country row
        countryRows.push(
          <CountryRow
            key={countryCode}
            countryName={name}
            countryCode={countryCode}
            rowControls={
              <ConfigRowControls
                data-test={countryCode}
                disabled={
                  disabled || (restrictedCountries.includes(countryCode) && countryIsDisabled)
                }
                hasAccessForBasicTier={hasAccessForBasicTier}
                rowDisabled={countryIsDisabled}
                handleEnable={() => handleEnableCountry(countryCode)}
                handleDisable={() => handleDisableCountry(countryCode)}
                handleResetToDefault={() => handleResetCountryToDefault(countryCode)}
              />
            }
            countryIsDisabled={countryIsDisabled}
            isCountryChecked={checkedCountries.includes(countryCode)}
            isExpanded={expandedCountries[countryCode]}
            disabled={disabled}
            onCountryCheck={onCountryCheck}
            restrictedCountries={restrictedCountries}
            toggleCountryRow={toggleCountryRow}
          />,
        );

        if (!countryIsDisabled && expandedCountries[countryCode]) {
          const enabledIdClasses = new Set(countryToIdClasses[countryCode]);
          const allIdClasses = defaultCountryIdClasses[countryCode];

          allIdClasses.forEach((idClassConfig) => {
            const idClassIsDisabled = !enabledIdClasses.has(idClassConfig.class);
            const idx = countryToIdClasses[countryCode].findIndex(
              (idClass) => idClass === idClassConfig.class,
            );

            countryRows.push(
              <IdRow
                idClass={idClassConfig.class}
                idClassIsDisabled={idClassIsDisabled}
                key={`${countryCode}-${idClassConfig.class}`}
                countryCode={countryCode}
                rowControls={
                  hasAccessForBasicTier && (
                    <ConfigRowControls
                      data-test={idClassConfig.class as string}
                      disabled={disabled}
                      hasAccessForBasicTier={hasAccessForBasicTier}
                      rowDisabled={idClassIsDisabled}
                      handleEnable={() => handleEnableIdClass(countryCode, idClassConfig.class)}
                      handleDisable={() => handleDisableIdClass(countryCode, idClassConfig.class)}
                      handleResetToDefault={() =>
                        handleResetIdClassToDefault(countryCode, idClassConfig.class)
                      }
                    />
                  )
                }
                idConfigSection={
                  !idClassIsDisabled && (
                    <IdRowConfigSection
                      idx={idx}
                      idClass={idClassConfig.class}
                      idClassOptions={enabledIdClasses}
                      countryCode={countryCode}
                      extractionIndex={extractionIndex}
                      expirationIndex={expirationIndex}
                      disabled={disabled || !hasAccessForBasicTier}
                    />
                  )
                }
              />,
            );
          });
        }

        allRows.push(...countryRows);
        countryIsDisabled ? disabledRows.push(...countryRows) : enabledRows.push(...countryRows);
      }
    });

    // This allows sorting/filtering the disabled countries
    return { allRows, enabledRows, disabledRows };
  }, [
    allCountries,
    checkedCountries,
    countryToIdClasses,
    defaultCountryIdClasses,
    expandedCountries,
    expirationIndex,
    extractionIndex,
    searchResults,
    disabled,
    hasAccessForBasicTier,
    handleDisableCountry,
    handleDisableIdClass,
    handleEnableCountry,
    handleEnableIdClass,
    handleResetCountryToDefault,
    handleResetIdClassToDefault,
    onCountryCheck,
    restrictedCountries,
    toggleCountryRow,
  ]);

  const rowsToShow = useMemo(
    () => filteredRows({ allRows, enabledRows, disabledRows, statusFilter, restrictedCountries }),
    [allRows, enabledRows, disabledRows, restrictedCountries, statusFilter],
  );

  // Note: For templates that still have restricted countries enabled (and for when customers enable through Persona),
  // we still want to maintain the configurations so that they may make changes to supported ids for these countries.
  const availableCountries = useMemo(
    () =>
      rowsToShow
        .filter(
          (row) =>
            !restrictedCountries.includes(row.props.countryCode) ||
            (restrictedCountries.includes(row.props.countryCode) && !row.props.countryIsDisabled),
        )
        .map((row) => row.props.countryCode),
    [restrictedCountries, rowsToShow],
  );

  // filter checked countries to edit based on available countries rendered by filter
  const filteredCheckedCountries = useMemo(() => {
    if (checkedCountries.length === 0) return [];
    const availableCheckedCountries = checkedCountries.filter((country) =>
      availableCountries.includes(country),
    );

    return availableCheckedCountries;
  }, [availableCountries, checkedCountries]);

  const toggleCheckedCountries = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setCheckedCountries(availableCountries);
      } else {
        setCheckedCountries([]);
      }
    },
    [setCheckedCountries, availableCountries],
  );

  const enableCheckedCountries = useCallback(() => {
    input.onChange(
      produce(input.value, (draft: VerificationTemplateVersionGovernmentIdAttributes) => {
        checkedCountries.forEach((countryCode) => {
          if (!enabledIdClassesByCountry[countryCode]) {
            draft['enabled-id-classes-by-country'][countryCode] =
              defaultCountryIdClasses[countryCode];
          }
        });
      }),
    );
    setCheckedCountries([]);
  }, [input, checkedCountries, enabledIdClassesByCountry, defaultCountryIdClasses]);

  const disableCheckedCountries = useCallback(() => {
    input.onChange(
      setEnabledIdClassesByCountry(omit(checkedCountries, enabledIdClassesByCountry), input.value),
    );
    setCheckedCountries([]);
    setExpandedCountries({} as ExpandedCountriesRecord);
  }, [input, setEnabledIdClassesByCountry, checkedCountries, enabledIdClassesByCountry]);

  const hasUsedFilters = searchTerm !== '' || statusFilter !== 'all';
  const disableBulkActionButtons = checkedCountries.length === 0;
  const tableContextMenuItems = [
    {
      id: 'reset',
      label: 'Reset to defaults',
      onClick: handleResetAllToDefault,
    },
    {
      id: 'restrict',
      label: 'Disable restricted countries',
      onClick: handleDisableRestrictedCountries,
    },
    {
      id: 'enable_extraction',
      label: 'Only enable countries and IDs with extraction support',
      hidden: !hasAccessForBasicTier,
      onClick: handleOnlyEnableCountriesWithExtractionSupport,
    },
    {
      id: 'disable',
      variant: 'danger' as const,
      label: 'Disable all',
      onClick: handleDisableAll,
    },
  ];

  return (
    <TableLayout
      showHeader
      aria-role='treegrid'
      isEmpty={rowsToShow.length === 0}
      containerMaxHeight={500}
      nullState={
        <TableNullState
          hasUsedFilters={hasUsedFilters}
          dataTestBlock='country-configurations-table__null-state'
        >
          All ID classes are allowed for all countries.
        </TableNullState>
      }
      headerTR={
        <>
          <TableTH isSticky>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <StyledCheckbox
                id='countries_all_check'
                indeterminate={
                  filteredCheckedCountries.length > 0 &&
                  filteredCheckedCountries.length !== availableCountries.length
                }
                disabled={disabled}
                checked={filteredCheckedCountries.length > 0}
                onChange={toggleCheckedCountries}
              />
              <Label size='lg' htmlFor='countries_all_check' weight='bold'>
                {' '}
                Countries
              </Label>
            </div>
          </TableTH>
          <TableTH isSticky />
          <TableTHWithIcon isSticky>
            <MenuWrapper>
              <div role='menubar'>
                <TableHeaderButton
                  id='country-configurations-table__enable-ids-button'
                  aria-label='Enable'
                  onClick={enableCheckedCountries}
                  disabled={disableBulkActionButtons}
                >
                  Enable
                </TableHeaderButton>
                <TableHeaderButton
                  id='country-configurations-table__disable-ids-button'
                  aria-label='Disable'
                  onClick={disableCheckedCountries}
                  disabled={disableBulkActionButtons}
                >
                  Disable
                </TableHeaderButton>
                {hasAccessForBasicTier && (
                  <TableHeaderButton
                    id='country-configurations-table__configure-ids-button'
                    aria-label='Configure IDs'
                    onClick={showConfigureIdsModal}
                    disabled={disableBulkActionButtons}
                  >
                    Configure IDs
                  </TableHeaderButton>
                )}
              </div>
              <ContextMenu
                disabled={disabled}
                items={tableContextMenuItems}
                renderItem={(item) => (
                  <ContextMenu.Item
                    key={item.id}
                    id={`country-configurations-table-header__action-menu-${item.id}`}
                    onClick={(e) => {
                      // Needed to avoid triggering row click below
                      e.stopPropagation();
                      e.preventDefault();
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      item.onClick();
                    }}
                    variant={item.variant}
                  >
                    {item.label}
                  </ContextMenu.Item>
                )}
              />
            </MenuWrapper>
          </TableTHWithIcon>
        </>
      }
    >
      {rowsToShow}
      <ModalWrapper isOpen={displayConfigureIdsModal} handleCloseModal={hideConfigureIdsModal}>
        <ConfigureIdsTable
          checkedCountries={checkedCountries}
          defaultCountryIdClasses={defaultCountryIdClasses}
          enabledIdClassesByCountry={enabledIdClassesByCountry}
          onApply={(updatedConfigSupport: GovernmentIdEnabledIdClassByCountries) => {
            const newlyDisabledCountries: CountryCodeLower[] = Object.entries(expandedCountries)
              .filter(
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                ([country, isExpanded]: [CountryCodeLower, boolean]) =>
                  !updatedConfigSupport[country] && isExpanded,
              )
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              .map(([country, _isExpanded]: [CountryCodeLower, boolean]) => country);

            setExpandedCountries(
              omit(newlyDisabledCountries, expandedCountries) as ExpandedCountriesRecord,
            );

            input.onChange(setEnabledIdClassesByCountry(updatedConfigSupport, input.value));
          }}
          hideConfigureIdsModal={hideConfigureIdsModal}
        />
      </ModalWrapper>
    </TableLayout>
  );
});

const TableTDWithSubRows = styled(TableTD).attrs(() => ({ ariaRole: 'row' }))`
  &[aria-expanded='true'] {
    border-bottom: none;
  }
`;

const StyledTableActionTD = styled(TableActionTD)`
  padding: ${Spacing[3]} 0px ${Spacing[3]} 0px;
  border-bottom: none;
`;

const TableActionTDWithSubRows = styled(TableActionTD).attrs(() => ({ ariaRole: 'row' }))`
  &[aria-expanded='true'] {
    border-bottom: none;
  }
`;

const CountryRowButton = styled.button.attrs(() => ({ type: 'button' }))`
  display: flex;
  height: 100%;
  width: 100%;
  flex: 1 0 auto;
  align-items: center;
  background: none;
  cursor: pointer;
  border: 0;
`;

const CountrySticker = styled(Sticker)`
  display: flex;
  justify-content: center;
  width: ${Spacing[6]};
`;

interface CountryRowProps {
  countryName: string;
  countryCode: CountryCodeLower;
  rowControls: React.ReactNode;
  countryIsDisabled: boolean;
  isCountryChecked: boolean;
  isExpanded: boolean;
  disabled: boolean;
  onCountryCheck: (e: ChangeEvent<HTMLInputElement>, countryCode: CountryCodeLower) => void;
  restrictedCountries: CountryCodeLower[];
  toggleCountryRow: (countryCode: CountryCodeLower) => void;
}

const CountryRow = memo(function CountryRow({
  countryName,
  countryCode,
  rowControls,
  countryIsDisabled,
  isCountryChecked,
  isExpanded,
  disabled,
  onCountryCheck,
  restrictedCountries,
  toggleCountryRow,
}: CountryRowProps) {
  // used for resetting country override defaults
  const ariaExpanded = isExpanded && !countryIsDisabled ? 'true' : 'false';
  const isRestrictedCountry = restrictedCountries.includes(countryCode);
  return (
    <TableTR aria-label={`${countryName} row`}>
      <TableTDWithSubRows aria-expanded={ariaExpanded}>
        <CountryNameContainer>
          <StyledCheckbox
            checked={isCountryChecked}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onCountryCheck(e, countryCode);
            }}
            disabled={(isRestrictedCountry && countryIsDisabled) || disabled}
          />
          <CountryRowButton
            id={`country-configurations-table-row__${countryCode}`}
            aria-label={`${countryName} row button`}
            disabled={countryIsDisabled}
            onClick={() => toggleCountryRow(countryCode)}
          >
            <CaretContainer>
              <StyledIconCaret expanded={ariaExpanded === 'true'} disabled={countryIsDisabled} />
            </CaretContainer>
            <Spacer height={0} width={1.5} />
            <CountrySticker variant={countryIsDisabled ? 'basic' : 'primary'}>
              {countryCode.toUpperCase()}
            </CountrySticker>
            <Spacer height={0} width={1} />
            <Label size='lg' color={countryIsDisabled ? 'disabled' : undefined} weight='bold'>
              {countryName}
            </Label>
            <Spacer height={0} width={1} />
            {isRestrictedCountry && countryIsDisabled ? (
              <Tooltip side='right' iconTrigger={<CircleHelpIcon />}>
                <Paragraph>
                  Verifications for this country can be configured by contacting Persona.
                </Paragraph>
              </Tooltip>
            ) : null}
          </CountryRowButton>
        </CountryNameContainer>
      </TableTDWithSubRows>
      <TableTDWithSubRows aria-expanded={ariaExpanded} />
      <TableActionTDWithSubRows aria-expanded={ariaExpanded}>
        {rowControls}
      </TableActionTDWithSubRows>
    </TableTR>
  );
});

interface IdRowProps {
  idClass: GovernmentIdClass;
  idClassIsDisabled: boolean;
  countryCode: CountryCodeLower;
  rowControls: React.ReactNode;
  idConfigSection: React.ReactNode;
}

const IdRow = memo(function IdRow({
  idClass,
  idClassIsDisabled,
  countryCode,
  rowControls,
  idConfigSection,
}: IdRowProps) {
  // See front-end/lib/constants/governmentIds.ts RE 'Unknown ID fallback'
  const idClassDisplay =
    idClass != null ? GOVERNMENT_ID_DISPLAY_STRINGS[idClass] : 'Unknown ID Type';
  return (
    <>
      <TableSubRow
        aria-label={`${idClassDisplay} row`}
        id={`country-configurations-table-row__${countryCode}-${idClass}`}
      >
        <SubRowLabelTD>
          <Label size='lg' color={idClassIsDisabled ? 'disabled' : undefined} weight='bold'>
            {idClassDisplay}
          </Label>
        </SubRowLabelTD>
        <TableTDWithSubRows aria-expanded='true' />
        <StyledTableActionTD>{rowControls}</StyledTableActionTD>
      </TableSubRow>
      {idConfigSection}
      <TableSubRow>
        <FullSpanSubRowTD colSpan={3} isLast={true} />
      </TableSubRow>
    </>
  );
});

const requiredSidesValidation = [
  withMessage(
    'Only one of back or barcode can be required for a given ID class',
    (requiredSides: GovernmentIdSide[] | undefined) => {
      const currentSides = requiredSides ?? [];
      return !(
        currentSides.includes(GovernmentIdSide.Back) &&
        currentSides.includes(GovernmentIdSide.BarcodePdf417)
      );
    },
  ),
];

/**
 * IdRow configurable fields:
 * required sides, extraction overrides, expiration overrides
 */
const IdRowConfigSection = memo(function IdRowConfigSection({
  idx,
  idClass,
  idClassOptions,
  countryCode,
  extractionIndex,
  expirationIndex,
  disabled,
}: {
  idx: number;
  idClass: GovernmentIdClass;
  idClassOptions: Set<GovernmentIdClass>;
  countryCode: CountryCodeLower;
  extractionIndex: number;
  expirationIndex: number;
  disabled: boolean;
}) {
  if (idClass == null) return null;
  const sidesOptions = REQUIRES_SIDES_OPTIONS.filter((sideOption) => {
    if (idClass === 'pp') {
      return [GovernmentIdSide.Front, GovernmentIdSide.PassportSignature].includes(
        sideOption.value,
      );
    }
    if (['dl', 'id'].includes(idClass) && ['ca', 'us'].includes(countryCode)) {
      return [
        GovernmentIdSide.Front,
        GovernmentIdSide.Back,
        GovernmentIdSide.BarcodePdf417,
      ].includes(sideOption.value);
    }
    return [GovernmentIdSide.Front, GovernmentIdSide.Back].includes(sideOption.value);
  });
  return (
    <>
      <TableSubRow aria-label={`${GOVERNMENT_ID_DISPLAY_STRINGS[idClass]} required sides row`}>
        <FullSpanSubRowTD>
          <FieldWrapper>
            <Field
              label={
                <SubRowLabel>
                  Required sides
                  <HelpIcon>
                    If back is required by default, this is usually due to important properties
                    (e.g., ID number, key dates) appearing only on the back of the ID. Use caution
                    when updating the default setting, since it may cause checks to fail
                    unexpectedly.
                  </HelpIcon>
                </SubRowLabel>
              }
              component={ReactSelectField}
              name={`attributes.enabled-id-classes-by-country.${countryCode}.${idx}.requires-sides`}
              id={`country-configurations-table-row__${countryCode}-${idClass}--requires-sides`}
              validate={requiredSidesValidation}
              options={sidesOptions}
              disabled={disabled}
              isMulti
            />
          </FieldWrapper>
        </FullSpanSubRowTD>
      </TableSubRow>
      <TableSubRow aria-label={`${GOVERNMENT_ID_DISPLAY_STRINGS[idClass]} accepted ID classes row`}>
        <FullSpanSubRowTD>
          <FieldWrapper>
            <Field
              size='md'
              component={ReactSelectField}
              label={<SubRowLabel>Accepted ID classes</SubRowLabel>}
              name={`attributes.enabled-id-classes-by-country.${countryCode}.${idx}.accepted-classes`}
              options={Array.from(idClassOptions).map((idClass) => ({
                label: idClass ? GOVERNMENT_ID_DISPLAY_STRINGS[idClass] : '',
                value: idClass,
              }))}
              disabled={disabled}
              isMulti
            />
          </FieldWrapper>
        </FullSpanSubRowTD>
      </TableSubRow>
      <FormSection name='attributes.verification-checks' component={Fragment}>
        {extractionIndex !== -1 && (
          <Field
            name={`${extractionIndex}.required-properties`}
            baseName={`${extractionIndex}.required-properties`}
            component={OverrideExtractionRow}
            idClass={idClass}
            countryCode={countryCode}
            label='Override required attributes'
            disabled={disabled}
          />
        )}
        {expirationIndex !== -1 && (
          <Field
            name={`${expirationIndex}.expiration-thresholds`}
            baseName={`${expirationIndex}.expiration-thresholds`}
            component={OverrideExpirationRow}
            idClass={idClass}
            countryCode={countryCode}
            label='Adjust expiration threshold'
            disabled={disabled}
          />
        )}
      </FormSection>
    </>
  );
});

interface ConfigRowControlsProps {
  'data-test': string;
  disabled: boolean;
  hasAccessForBasicTier: boolean;
  rowDisabled: boolean;
  handleEnable: () => void;
  handleDisable: () => void;
  handleResetToDefault: () => void;
}

/**
 * shared controls for enabling, disabling, resetting defaults
 */
const ConfigRowControls = memo(function ConfigRowControls({
  'data-test': dataTest,
  disabled,
  hasAccessForBasicTier,
  rowDisabled,
  handleEnable,
  handleDisable,
  handleResetToDefault,
}: ConfigRowControlsProps) {
  return rowDisabled ? (
    <Button
      width='icon'
      variant='basic'
      id={`country-configurations-table-row__${dataTest}--enable`}
      data-test={`country-configurations-table-row__${dataTest}--enable`}
      onClick={handleEnable}
      disabled={disabled}
    >
      <PlusIcon />
    </Button>
  ) : (
    <ContextMenu
      disabled={disabled}
      items={[
        {
          id: 'reset',
          label: 'Use defaults',
          hidden: !hasAccessForBasicTier,
          onClick: handleResetToDefault,
        },
        {
          id: 'delete',
          variant: 'danger' as const,
          label: 'Disable',
          onClick: handleDisable,
        },
      ]}
      renderItem={(item) => (
        <ContextMenu.Item
          key={item.id}
          id={`country-configurations-table-row__${dataTest}--action-menu-${item.id}`}
          onClick={(e) => {
            // Needed to avoid triggering row click below
            e.stopPropagation();
            e.preventDefault();
            if (item && item.onClick) item.onClick();
          }}
          variant={item.variant}
        >
          {item.label}
        </ContextMenu.Item>
      )}
    />
  );
});

export default FormSectionCountryConfigurations;
